
import { Component, Vue } from 'vue-property-decorator'
import axios from 'axios'
import { notification } from 'ant-design-vue'
import { ApexOptions } from 'apexcharts'
import PageHeader from '@/components/base/PageHeader.vue'

type KAM004RiskItem = { key: string; label: string; aandachtspunt: number; onvoldoende: number; action_count: number; total: number }
type DashboardAction = {
  id: string
  form_id: number
  form_reference: string
  form_name: string
  project: string
  project_reference: string
  description: string
  owner: string
  due_date: string
  overdue: boolean
}
type KAM004Trend = { label: string; voldoende: number; onvoldoende: number }
type KAM004Dashboard = {
  summary: {
    total: number
    this_month: number
    open_actions: number
    overdue_actions: number
    actions_without_owner: number
    actions_without_due_date: number
    insufficient_without_open_action: number
    insufficient_forms: number
  }
  risk_items: KAM004RiskItem[]
  actions: DashboardAction[]
  trend: KAM004Trend[]
}
type KAMDashboardData = {
  open_forms: number
  closed_forms: number
  total_incidents: number
  sickness_absence_incidents: number
  action_summary: {
    open_actions: number
    overdue_actions: number
    actions_without_owner: number
    actions_without_due_date: number
  }
  actions: DashboardAction[]
  kam004: KAM004Dashboard
}

const emptyKam004 = (): KAM004Dashboard => ({
  summary: {
    total: 0,
    this_month: 0,
    open_actions: 0,
    overdue_actions: 0,
    actions_without_owner: 0,
    actions_without_due_date: 0,
    insufficient_without_open_action: 0,
    insufficient_forms: 0
  },
  risk_items: [],
  actions: [],
  trend: []
})

@Component({
  components: { PageHeader }
})
export default class KAMDashboard extends Vue {
  loading = false

  dashboard: KAMDashboardData = {
    open_forms: 0,
    closed_forms: 0,
    total_incidents: 0,
    sickness_absence_incidents: 0,
    action_summary: {
      open_actions: 0,
      overdue_actions: 0,
      actions_without_owner: 0,
      actions_without_due_date: 0
    },
    actions: [],
    kam004: emptyKam004()
  }

  riskColumns = [
    { title: 'Onderdeel', dataIndex: 'label', key: 'label', width: 320 },
    { title: 'Onv. / Aand.', key: 'score', scopedSlots: { customRender: 'score' }, width: 130 },
    { title: 'Acties', dataIndex: 'action_count', key: 'action_count', width: 90 },
    { title: 'Beoordelingen', dataIndex: 'total', key: 'total', width: 120 }
  ]

  actionColumns = [
    { title: 'Formulier', key: 'form', scopedSlots: { customRender: 'form' }, width: 120 },
    { title: 'Project', key: 'project', scopedSlots: { customRender: 'project' }, width: 180 },
    { title: 'Actiepunt', dataIndex: 'description', key: 'description', width: 360 },
    { title: 'Eigenaar', dataIndex: 'owner', key: 'owner', width: 160 },
    { title: 'Termijn', key: 'due_date', scopedSlots: { customRender: 'due_date' }, width: 130 }
  ]

  mounted (): void {
    this.loadDashboard()
  }

  async loadDashboard (): Promise<void> {
    this.loading = true

    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/kamForms/dashboard`)
      const kam004 = response.data.kam004 || {}
      this.dashboard = {
        ...response.data,
        action_summary: {
          open_actions: 0,
          overdue_actions: 0,
          actions_without_owner: 0,
          actions_without_due_date: 0,
          ...(response.data.action_summary || {})
        },
        actions: response.data.actions || [],
        kam004: {
          ...emptyKam004(),
          ...kam004,
          summary: {
            ...emptyKam004().summary,
            ...(kam004.summary || {})
          }
        }
      }
    } catch (error) {
      notification.error({ message: 'Fout tijdens het ophalen van het KAM dashboard!', description: 'Er is iets mis gegaan. Probeer het later nog een keer.', duration: 3 })
    } finally {
      this.loading = false
    }
  }

  get trendChartOptions (): ApexOptions {
    return {
      chart: { toolbar: { show: false }, stacked: false },
      colors: ['#52c41a', '#f5222d'],
      dataLabels: { enabled: false },
      legend: { position: 'top' },
      plotOptions: { bar: { borderRadius: 2, columnWidth: '48%' } },
      xaxis: { categories: this.dashboard.kam004.trend.map(item => item.label) },
      yaxis: { min: 0, forceNiceScale: true }
    }
  }

  get trendChartSeries (): Array<{ name: string; data: number[] }> {
    const trend = this.dashboard.kam004.trend
    return [
      { name: 'Voldoende', data: trend.map(item => item.voldoende) },
      { name: 'Onvoldoende', data: trend.map(item => item.onvoldoende) }
    ]
  }

  dateLabel (value: string): string {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('nl-NL')
  }

  navigateToForm (id: number, formReference: string): void {
    this.$router.push({ name: 'kamFormEdit', params: { kamFormId: id.toString(), formReference: formReference || 'KAM' } })
  }

  onActionRow (record: DashboardAction): Record<string, unknown> {
    return { on: { click: () => this.navigateToForm(record.form_id, record.form_reference) } }
  }
}
